import React from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { ApiService } from '../../hooks';
import { notifications, extractApiErrorMessage } from '../../utils';
import { formatDateTime } from '../../utils/dateHelper';
import { Loader, Title, LegacyGrid as Grid } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip
} from '@mui/material';
import { tokens } from '../../theme/tokens';
import { V4_DIALOG_CONTENT, V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import {
  ANIMAL_EXTERNAL_IDENTIFIER_TYPES,
  AnimalExternalIdentifierType,
  AnimalExternalIdentifierTypeLabelKeys,
  AnimalExternalIdentifierViewModel,
  CreateAnimalExternalIdentifierRequest,
  DEFAULT_ANIMAL_EXTERNAL_IDENTIFIER_TYPE,
  isAnimalExternalIdentifierType
} from '../../models/animal-external-identifier';

interface AnimalViewExternalIdsProps {
  animalId: string;
  onReload?: () => void;
}

const AnimalViewExternalIds: React.FC<AnimalViewExternalIdsProps> = ({ animalId, onReload }) => {
  const { t } = useTranslation();
  const confirmDialog = useConfirmation();
  const apiService = new ApiService();

  // Data state
  const [externalIdentifiers, setExternalIdentifiers] = React.useState<AnimalExternalIdentifierViewModel[]>([]);
  const [total, setTotal] = React.useState(0);

  // Loading state
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  // Pagination state
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);

  // Search state
  const [searchTerm, setSearchTerm] = React.useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [formName, setFormName] = React.useState('');
  const [formType, setFormType] = React.useState<AnimalExternalIdentifierType>(DEFAULT_ANIMAL_EXTERNAL_IDENTIFIER_TYPE);
  const [formValue, setFormValue] = React.useState('');
  const [formErrors, setFormErrors] = React.useState<{ name?: string; value?: string }>({});

  // Load external identifiers from API
  const loadExternalIdentifiers = async () => {
    if (!animalId) {
      notifications.error('Animal ID is undefined.');
      return;
    }

    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    try {
      const result = await apiService.api.animals.animalsGetExternalIdentifiers({
        id: animalId,
        pageIndex: searchPage,
        pageSize: rowsPerPage,
        searchTerm: searchTerm || undefined
      });

      // The generated client does not carry `type` yet (see models/animal-external-identifier.ts).
      // Drop this cast once `bun run swagger` is re-run against the updated backend.
      setExternalIdentifiers((result.data.items || []) as AnimalExternalIdentifierViewModel[]);
      setTotal(result.data.totalCount || 0);
      setLoaded(true);
    } catch (e: unknown) {
      notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      setLoaded(true);
    } finally {
      setIsBusy(false);
    }
  };

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };

  // Handle search
  const handleSearch = () => {
    setPage(0);
    setLoaded(false);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    setPage(0);
    setLoaded(false);
  };

  // Open add dialog
  const openAddDialog = () => {
    setDialogOpen(true);
  };

  // Close dialog and reset form
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormName('');
    setFormType(DEFAULT_ANIMAL_EXTERNAL_IDENTIFIER_TYPE);
    setFormValue('');
    setFormErrors({});
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: { name?: string; value?: string } = {};

    if (!formName || formName.trim().length === 0) {
      errors.name = t('f-external-id-name-required');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save external identifier
  const handleSaveExternalId = async () => {
    if (!validateForm()) {
      return;
    }

    setIsBusy(true);

    try {
      // Typed through the local request shim so the new `type` field survives until the generated
      // client is regenerated (see models/animal-external-identifier.ts).
      const payload: CreateAnimalExternalIdentifierRequest = {
        name: formName.trim(),
        type: formType,
        value: formValue.trim() || null
      };

      await apiService.api.animals.animalsCreateExternalIdentifier({ id: animalId }, payload);

      notifications.success(t('f-external-id-added'));
      handleCloseDialog();
      setLoaded(false);

      if (onReload) {
        onReload();
      }
    } catch (e: unknown) {
      notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
    } finally {
      setIsBusy(false);
    }
  };

  // Delete external identifier
  const handleDeleteExternalId = (item: AnimalExternalIdentifierViewModel) => {
    confirmDialog({
      dialogId: 'delete-external-id-dialog',
      title: t('f-external-id-confirm-delete-title'),
      message: t('f-external-id-confirm-delete-text', { name: item.name, value: item.value }),
      alertSeverity: 'warning'
    }).then(async () => {
      setIsBusy(true);

      try {
        await apiService.api.animals.animalsDeleteExternalIdentifier({ id: animalId, externalIdentifierId: item.id });
        notifications.success(t('f-external-id-deleted'));
        setLoaded(false);

        if (onReload) {
          onReload();
        }
      } catch (e: unknown) {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      } finally {
        setIsBusy(false);
      }
    });
  };

  // Render the identifier type: known values get a translated chip, unrecognised ones show the raw
  // value, and a missing type (older records, or a backend that does not send it yet) shows a dash.
  const renderTypeCell = (type: AnimalExternalIdentifierViewModel['type']) => {
    if (!type) {
      return '-';
    }

    const label = isAnimalExternalIdentifierType(type) ? t(AnimalExternalIdentifierTypeLabelKeys[type]) : type;

    return <Chip size="small" variant="outlined" label={label} />;
  };

  // Load data when loaded flag changes
  React.useEffect(() => {
    if (!loaded) {
      loadExternalIdentifiers();
    }
  }, [loaded]);

  // Reset state when animalId changes
  React.useEffect(() => {
    setExternalIdentifiers([]);
    setTotal(0);
    setPage(0);
    setSearchTerm('');
    setLoaded(false);
  }, [animalId]);

  return (
    <Paper elevation={1} sx={{ p: 2, display: 'flex', overflow: 'auto', flexDirection: 'column', minHeight: 200 }}>
      <Grid container spacing={1}>
        {/* Title and Actions Row */}
        <Grid container direction="row">
          <Grid container item spacing={1} xs={10}>
            <Grid item>
              <Icon color="primary">{'label'}</Icon>
            </Grid>
            <Grid item>
              <Title>{t('f-external-id-title')}</Title>
            </Grid>
            <Grid item>
              <Chip size="small" variant="outlined" label={`${t('f-com-total')}: ${total}`} />
            </Grid>
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'right', flex: 1 }}>
            <Tooltip arrow title={t('f-external-id-add-button')}>
              <IconButton
                id="add-external-id-btn"
                aria-label="add"
                color="primary"
                size="small"
                sx={V4_ICON_BUTTON_SMALL}
                onClick={openAddDialog}
              >
                <Icon>{'add_circle_outline'}</Icon>
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Search Section */}
        <Grid item xs={12}>
          <Grid container spacing={1} sx={{ mt: 2, mb: 2, alignItems: 'center' }}>
            <Grid item>
              <TextField
                label={t('f-external-id-search')}
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>{'search'}</Icon>
                      </InputAdornment>
                    )
                  }
                }}
              />
            </Grid>

            <Grid item>
              <Button
                id="searchBtn"
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSearch}
                startIcon={<Icon>{'search'}</Icon>}
              >
                {t('f-search')}
              </Button>
            </Grid>

            <Grid item>
              <Tooltip title={t('f-com-filter-clear')} arrow>
                <IconButton size="small" sx={V4_ICON_BUTTON_SMALL} color="primary" onClick={clearSearch}>
                  <Icon>{'clear'}</Icon>
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>

        {/* Table */}
        <Grid item xs={12}>
          <Paper>
            <Table size="small" sx={V4_TABLE} aria-label="external identifiers table">
              <TableHead sx={{ background: tokens.sidebar }}>
                <TableRow>
                  <TableCell>{t('f-external-id-name')}</TableCell>
                  <TableCell>{t('f-external-id-type')}</TableCell>
                  <TableCell>{t('f-external-id-value')}</TableCell>
                  <TableCell>{t('f-com-created-at')}</TableCell>
                  <TableCell>{t('f-com-updated-at')}</TableCell>
                  <TableCell align="right">{t('f-com-action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {externalIdentifiers.length > 0 ? (
                  externalIdentifiers.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{renderTypeCell(item.type)}</TableCell>
                      <TableCell>{item.value}</TableCell>
                      <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                      <TableCell>{formatDateTime(item.updatedAt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip arrow title={t('f-external-id-delete')}>
                          <IconButton
                            aria-label="delete"
                            color="secondary"
                            size="small"
                            sx={V4_ICON_BUTTON_SMALL}
                            onClick={() => handleDeleteExternalId(item)}
                          >
                            <Icon>delete</Icon>
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t('f-external-id-empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </Paper>
          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
        </Grid>
      </Grid>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} aria-labelledby="form-dialog-title">
        <DialogTitle id="add-external-id-dialog" sx={{ background: tokens.sidebar }}>
          {t('f-external-id-add-title')}
        </DialogTitle>

        <DialogContent sx={V4_DIALOG_CONTENT}>
          <TextField
            autoFocus
            required
            margin="dense"
            // v4's dense standard field; v9 splits the density into size and defaults to outlined.
            variant="standard"
            size="small"
            id="external-id-name"
            label={t('f-external-id-name')}
            type="text"
            value={formName}
            onChange={(e) => {
              setFormName(e.target.value);
              setFormErrors({ ...formErrors, name: undefined });
            }}
            error={!!formErrors.name}
            helperText={formErrors.name}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon>{'label'}</Icon>
                  </InputAdornment>
                )
              }
            }}
          />

          <FormControl fullWidth margin="dense" variant="standard" size="small">
            <InputLabel id="external-id-type-label">{t('f-external-id-type')}</InputLabel>
            <Select
              labelId="external-id-type-label"
              id="external-id-type"
              value={formType}
              onChange={(e) => setFormType(e.target.value as AnimalExternalIdentifierType)}
            >
              {ANIMAL_EXTERNAL_IDENTIFIER_TYPES.map((type) => (
                <MenuItem key={type} value={type} id={`external-id-type-${type}`}>
                  {t(AnimalExternalIdentifierTypeLabelKeys[type])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            // v4's dense standard field; v9 splits the density into size and defaults to outlined.
            variant="standard"
            size="small"
            id="external-id-value"
            label={t('f-external-id-value')}
            type="text"
            value={formValue}
            onChange={(e) => setFormValue(e.target.value)}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon>{'tag'}</Icon>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={t('f-external-id-value-tooltip')} arrow>
                      <Icon color="primary">{'info'}</Icon>
                    </Tooltip>
                  </InputAdornment>
                )
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ background: tokens.sidebar }}>
          <Button variant="outlined" startIcon={<Icon>{'close'}</Icon>} onClick={handleCloseDialog} color="primary">
            {t('f-com-cancel')}
          </Button>
          <Button
            onClick={handleSaveExternalId}
            startIcon={<Icon>{'save'}</Icon>}
            id="save-external-id-btn"
            variant="contained"
            color="primary"
            disabled={isBusy}
          >
            {t('f-com-save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AnimalViewExternalIds;
